// Duplicated from @shopee/authentication-redirect

// Constants
import { RedirectType, UrlMatchType } from './constants';

// Utils
import { parseUrlToObject } from '@shopee/url-parser';
import { parseQueryStringToObj } from '@shopee/url-util';

// Types
import { AuthenticationConfig, RedirectionInfo } from './types';

function getRelativeUrl(url: string): string {
  const { pathname, search, hash } = parseUrlToObject(url);

  return `${pathname}${search}${hash}`;
}

function isValidUrlByUrl(url: string, urlList: string[]): boolean {
  return urlList.some((_url: string) => _url === url);
}

function isValidUrlByHostname(
  url: string,
  regexList: RegExp[] | Array<{ hostname: RegExp; pathname: RegExp }>
): boolean {
  const { protocol, hostname } = parseUrlToObject(url);

  // https://cheatsheetseries.owasp.org/cheatsheets/Unvalidated_Redirects_and_Forwards_Cheat_Sheet.html
  // allow HTTP(S) protocol only
  if (protocol !== 'https:' && protocol !== 'http:') {
    return false;
  }

  return regexList.some((regex: any) => regex.test(hostname));
}

function isValidUrlByHostnameAndPathName(
  url: string,
  regexList: RegExp[] | Array<{ hostname: RegExp; pathname: RegExp }>
): boolean {
  const { protocol, hostname, pathname } = parseUrlToObject(url);

  // https://cheatsheetseries.owasp.org/cheatsheets/Unvalidated_Redirects_and_Forwards_Cheat_Sheet.html
  // allow HTTP(S) protocol only
  if (protocol !== 'https:' && protocol !== 'http:') {
    return false;
  }

  return regexList.some((regex: any) => {
    const { hostname: hostnameRegex, pathname: pathnameRegex } = regex;
    return hostnameRegex.test(hostname) && pathnameRegex.test(pathname);
  });
}
export function isValidUrl({
  url,
  authenticationConfig,
}: {
  url: string;
  authenticationConfig:
    | {
        matchType: UrlMatchType.ExactMatch;
        urlList: string[];
      }
    | {
        matchType: UrlMatchType.HostnameRegex;
        regexList: RegExp[];
      }
    | {
        matchType: UrlMatchType.HostnameAndPathnameRegex;
        regexList: Array<{ hostname: RegExp; pathname: RegExp }>;
      };
}) {
  // Disallow credentials (username/password) and invalid URLs
  try {
    const parsed = new URL(url);
    if (parsed.username || parsed.password) {
      return false;
    }
  } catch (e) {
    return false;
  }

  switch (authenticationConfig.matchType) {
    case UrlMatchType.ExactMatch:
      return isValidUrlByUrl(url, authenticationConfig.urlList);
    case UrlMatchType.HostnameRegex:
      return isValidUrlByHostname(url, authenticationConfig.regexList);
    case UrlMatchType.HostnameAndPathnameRegex:
      return isValidUrlByHostnameAndPathName(
        url,
        authenticationConfig.regexList
      );
    default:
      return false;
  }
}

function getRedirectUrl({
  url,
  authenticationConfig,
}: {
  url: string;
  authenticationConfig: AuthenticationConfig;
}): string | null {
  if (isValidUrl({ url, authenticationConfig })) {
    return url;
  } else {
    return null;
  }
}

type GetRedirectionInfoConfig = {
  redirectType: RedirectType;
  shouldReplaceHistory?: boolean;
  nextUrl: string;
};

// return next URL information
// return null if the params is not valid
function getRedirectionInfo({
  authenticationConfig,
  redirectType,
  shouldReplaceHistory = false,
  nextUrl,
}: GetRedirectionInfoConfig & {
  authenticationConfig: AuthenticationConfig;
}): RedirectionInfo {
  const redirectUrl = getRedirectUrl({
    url: nextUrl,
    authenticationConfig,
  });

  if (!redirectUrl) {
    return null;
  }

  return redirectType === RedirectType.Internal
    ? {
        url: getRelativeUrl(redirectUrl),
        redirect_type: redirectType as RedirectType,
        should_replace_history: shouldReplaceHistory,
      }
    : {
        url: redirectUrl,
        redirect_type: redirectType,
        should_replace_history: shouldReplaceHistory,
      };
}

// given the clientID, return next URL information
// return null if the params is not valid
export function getRedirectionInfoFromClientId({
  clientIdToRedirectionConfig,
  clientId,
  redirectType,
  shouldReplaceHistory = false,
  nextUrl,
}: GetRedirectionInfoConfig & {
  clientIdToRedirectionConfig: Record<string, AuthenticationConfig>;
  clientId: string;
}): RedirectionInfo {
  // check config for external redirection
  const authenticationConfig = clientId
    ? clientIdToRedirectionConfig[clientId]
    : clientIdToRedirectionConfig.default;

  if (!authenticationConfig) {
    return null;
  }

  return getRedirectionInfo({
    authenticationConfig,
    redirectType,
    shouldReplaceHistory,
    nextUrl,
  });
}

// return `next` parameters based on `client_id` from query string
// return null if the params is not valid
export function getRedirectionInfoFromQueryString(
  clientIdToRedirectionConfig: Record<string, AuthenticationConfig>,
  queryString: string
): RedirectionInfo {
  const {
    next = '',
    client_id = '',
    redirect_type = '',
    should_replace_history = '',
  } = parseQueryStringToObj(queryString);

  return getRedirectionInfoFromClientId({
    clientIdToRedirectionConfig,
    clientId: client_id,
    redirectType: redirect_type as RedirectType,
    shouldReplaceHistory: should_replace_history === 'true',
    nextUrl: next,
  });
}
